/**
 * Filing evidence — what we actually sent to a state, and what it actually said back.
 *
 * `dbo.FilingSubmissionEvidence` has recorded both sides of every submission since 2026-07-28, and
 * until now nothing could read it. A filer watching a policy sit in "Awaiting state" had no way to see
 * the payload or the reply; the state's acceptance — including the per-policy confirmation numbers a
 * regulator is quoted — was visible only on the clearinghouse's own website.
 *
 * "The system filed it" and "I can prove the system filed it" are different claims. This reads the
 * second one.
 *
 * ONE POLICY AT A TIME. The endpoint has no sweep mode by design: this is the only read in the filing
 * area that returns filing CONTENT (insured names, addresses, premiums) rather than status, so it is
 * role-gated server-side and scoped to a policy the caller names.
 *
 * APPEND-ONLY, SO ORDER IS THE STORY. A transaction usually has more than one attempt: the submit
 * writes `queued`, and reconciliation later appends the state's real answer against the same
 * submission number. Rows arrive oldest-first and should be rendered that way.
 */

import { apiClient } from './api';

/** Mirrors the CHECK on dbo.FilingSubmissionEvidence.Outcome. */
export type EvidenceOutcome = 'filed' | 'queued' | 'rejected' | 'conflict' | 'error';

export interface FilingEvidenceAttempt {
    evidenceId: number;
    state: string | null;
    stateId: number | null;
    customerPolicyId: number | null;
    transactionType: string | null;
    transEffDate: string | null;
    partnersId: number | null;
    filingSystem: string | null;
    outcome: EvidenceOutcome | string | null;
    submissionNumber: string | null;
    submissionStatus: string | null;
    detail: string | null;
    requestFileName: string | null;
    /** Bytes actually SENT. Compare against the rendered body — they differ when truncated. */
    requestBytes: number | null;
    responseBytes: number | null;
    requestBody: string | null;
    requestTruncated: boolean;
    responseBody: string | null;
    responseTruncated: boolean;
    requestBlobUri: string | null;
    responseBlobUri: string | null;
    correlationId: string | null;
    submittedAt: string | null;
    respondedAt: string | null;
    createdAt: string | null;
}

export interface FilingEvidenceResult {
    ok: boolean;
    /** True when the endpoint isn't deployed yet (501) — render "not available", not an error. */
    unavailable: boolean;
    /** True when the signed-in user may not read filing content (403). */
    forbidden: boolean;
    attempts: FilingEvidenceAttempt[];
    message?: string;
}

/**
 * How an outcome should read to a filer. Deliberately not reusing the batch-review lifecycle labels:
 * this describes ONE ATTEMPT at a moment in time, where the row describes where a policy stands now.
 * An attempt that was `queued` stays `queued` in history even after the state later accepts it — that
 * is the point of an append-only trail.
 */
export function evidenceOutcomeLabel(outcome: string | null | undefined): string {
    switch (String(outcome || '').trim().toLowerCase()) {
        case 'filed':    return 'Accepted by the state';
        case 'queued':   return 'Submitted — awaiting the state';
        case 'rejected': return 'Rejected by the state';
        case 'conflict': return 'Not sent — already reserved';
        case 'error':    return 'Failed to send';
        default:         return outcome ? String(outcome) : 'Unknown';
    }
}

/** Chip colours, matching the batch-review palette so the two screens agree visually. */
export function evidenceOutcomeStyle(outcome: string | null | undefined): { bg: string; fg: string } {
    switch (String(outcome || '').trim().toLowerCase()) {
        case 'filed':    return { bg: '#e6f4ea', fg: '#1e7e34' };
        case 'queued':   return { bg: '#e7f1fb', fg: '#1c5d99' };
        case 'rejected': return { bg: '#fdecea', fg: '#c62828' };
        case 'error':    return { bg: '#fdecea', fg: '#c62828' };
        case 'conflict': return { bg: '#fff4e5', fg: '#a15c07' };
        default:         return { bg: '#eef2f6', fg: '#475569' };
    }
}

export async function getFilingEvidence(
    token: string | undefined,
    customerPolicyId: number,
): Promise<FilingEvidenceResult> {
    const res: any = await apiClient(`/AutomaticFiling/filingEvidence?customerPolicyId=${encodeURIComponent(String(customerPolicyId))}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    });
    // apiClient returns the camelCased body on success and { ok:false, ...body } on an HTTP error, where
    // the failure body's own `status:false` clobbers the numeric HTTP status — so the two cases worth
    // distinguishing are detected from the message rather than a code.
    if (res && res.status === false) {
        const msg = String(res.message || '');
        return {
            ok: false,
            unavailable: /not available/i.test(msg),
            forbidden: /do not have access/i.test(msg),
            attempts: [],
            message: msg || 'Filing evidence could not be loaded.',
        };
    }
    const attempts = (res && res.data && Array.isArray(res.data.attempts)) ? res.data.attempts : [];
    return { ok: true, unavailable: false, forbidden: false, attempts };
}
